import {
  springConfig,
  TextChangeTransition,
} from 'src/components/common/TextChangeTransition';
import { AnimatePresence, motion, MotionProps } from 'framer-motion';
import styled from 'styled-components';
import React, { FC, useRef, useState } from 'react';
import { SearchFilters } from 'src/services/search';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { Field, FieldProps } from 'formik';
import { Button } from 'src/components/common/Button';
import { MediaDirect } from 'vendor/mods/media-breakpoints-fresnel/components/Media';
import Filter from 'src/components/icons/Filter';
import { FiltersBarHeader } from 'src/components/filters/FilterBlock';
import { FilterType } from 'src/components/filters/types';
import { FilterSearchInput } from 'src/components/filters/FilterSearchInput';
import { FormikAutoSubmit } from 'src/components/common/FormikAutoSubmit';
import clean from 'clean-deep';
import memoizee from 'memoizee';
import { ScrollableContent } from 'src/components/ScrollableContent';
import { layer } from 'src/styles/common';
import { dirRTL } from 'src/styles/theme';
import { FiltersBarMobile } from 'src/modules/materials/components/ShowcasePage/FiltersBar/FiltersBarMobile';
import { omit } from 'rambda';

const FiltersContainer = styled.aside`
  position: relative;
  padding: 20px 0;
  --left-usable-margin: 20px;
  --right-usable-margin: 20px;

  ${media.w.greaterThanOrEqual.desktop} {
    flex: 1 1 0;
    height: calc(100vh - var(--header-fixed-height));
    // If someone decides that it's super annoying that user cannot scroll all the way to the
    // filters bar bottom without scrolling over tvbar first, uncomment the two lines below and
    // add some z-index to tv-bar container.
    // Side-effect of it is that there will be white padding where tv-bar was
    // overlapping the filters bar.

    //margin-top: calc(var(--header-fixed-height) - var(--header-static-height));
    //padding-top: calc(
    //  20px + var(--header-static-height) - var(--header-fixed-height)
    //);
    position: sticky;
    top: var(--header-fixed-height);
    display: flex;
    flex-direction: column;
    min-height: 600px;
    margin-left: calc(-1 * var(--constraint-container-left-margin));
    z-index: 2;

    ${dirRTL} {
      margin-left: 0;
      margin-right: calc(-1 * var(--constraint-container-left-margin));
    }

    & > * {
      flex: 0 0 auto;
    }

    & > *:last-child {
      flex: 1 1 0;
      position: relative;
      & > * {
        ${layer}
      }
    }

    padding-right: 5px;
    padding-bottom: 0;

    &:before {
      z-index: 0;
      content: '';
      position: absolute;
      width: 100vh;
      height: 100%;
      top: 0;
      right: 0;
      background: ${({ theme }) => theme.palette.background};

      ${dirRTL} {
        right: auto;
        left: 0;
      }
    }
  }
`;

const FiltersBarInnerPadding = styled.div`
  ${media.w.greaterThanOrEqual.desktop} {
    padding-left: var(--constraint-container-left-margin);
    --left-usable-margin: var(--constraint-container-left-margin);

    padding-right: 30px;
    --right-usable-margin: 30px;
    ${dirRTL} {
      padding-right: var(--constraint-container-left-margin);
      padding-left: 30px;
    }
  }
`;

const SearchQueryInputContainer = styled.div`
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing.gap}px;
  grid-template-areas: 'input filtersButton' 'filters filters';

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-areas: 'input' 'filters';
  }
`;

const ClearButtonWrapper = styled(motion.div)`
  margin-left: 10px;
  ${dirRTL} {
    margin-left: 0;
    margin-right: 10px;
  }
`;

const keywordsTransitionProps = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { delay: 0.1 } },
  exit: { opacity: 0, x: 40 },
  transition: springConfig,
} as MotionProps;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledFilterSearchInput = styled(FilterSearchInput)`
  margin-bottom: 24px;
`;

const StyledMediaDirect = styled(MediaDirect)`
  grid-area: filtersButton;
`;

const getSelectedFiltersCount = memoizee(
  (filters: SearchFilters): number => {
    return Object.values(clean(omit('showcase', filters))).filter((v) =>
      Array.isArray(v) ? v.length > 0 : !!v,
    ).length;
  },
  {
    max: 1,
  },
);

export const FiltersBar: FC = ({ children }) => {
  const { _ } = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [filtersMobileOpen, setFiltersMobileOpen] = useState(false);

  const toggleFilters = () => setFiltersMobileOpen((v) => !v);

  return (
    <FiltersContainer ref={containerRef}>
      <FiltersBarInnerPadding>
        <Field>
          {({ form }: FieldProps) => {
            const selectedFiltersCount = getSelectedFiltersCount(form.values);
            const clearFilters = () => {
              form.setValues({});
            };
            return (
              <Row>
                <MediaDirect greaterThanOrEqual="desktop">
                  <FiltersBarHeader>
                    <TextChangeTransition letters>
                      {selectedFiltersCount > 0
                        ? _('Filters ({count})', undefined, {
                            count: selectedFiltersCount,
                          })
                        : _('Filters')}
                    </TextChangeTransition>
                  </FiltersBarHeader>
                  <AnimatePresence>
                    {selectedFiltersCount > 0 && (
                      <ClearButtonWrapper
                        {...keywordsTransitionProps}
                        layout="position"
                      >
                        <Button
                          kind="text"
                          variant="underline"
                          onClick={clearFilters}
                        >
                          {_('clear all')}
                        </Button>
                      </ClearButtonWrapper>
                    )}
                  </AnimatePresence>
                </MediaDirect>
              </Row>
            );
          }}
        </Field>

        <SearchQueryInputContainer>
          <StyledMediaDirect lessThan="desktop">
            <FilterSearchInput
              filter={{
                queryField: 'q',
                type: FilterType.SearchInput,
                title: _('Write a phrase'),
              }}
            />
            <Button
              kind="text"
              icon={Filter}
              onClick={toggleFilters}
              iconProps={{
                position: 'right',
              }}
            >
              {_('Filters')}
            </Button>
          </StyledMediaDirect>
        </SearchQueryInputContainer>
      </FiltersBarInnerPadding>

      <AnimatePresence>
        {filtersMobileOpen && (
          <MediaDirect lessThan="desktop">
            <FiltersBarMobile
              containerRef={containerRef}
              onClose={toggleFilters}
            >
              {children}
            </FiltersBarMobile>
          </MediaDirect>
        )}
      </AnimatePresence>

      <MediaDirect greaterThanOrEqual="desktop">
        <div>
          <ScrollableContent>
            <FiltersBarInnerPadding>
              <MediaDirect greaterThanOrEqual="desktop">
                <StyledFilterSearchInput
                  filter={{
                    queryField: 'q',
                    type: FilterType.SearchInput,
                    title: _('Write a phrase'),
                  }}
                />
              </MediaDirect>
              {children}
            </FiltersBarInnerPadding>
          </ScrollableContent>
        </div>
        <FormikAutoSubmit />
      </MediaDirect>
    </FiltersContainer>
  );
};
